import apiClient from "../../client/apiClient"
import { appendQueryParams } from "../../utils"

export type GenerateRollNumbersResult = {
  generated: number
  strategy: "section-wise" | "whole-class"
  action: "generate" | "regenerate" | "append"
  // `studentsNumbered` = total active students in the class; `newlyNumbered`
  // = how many of those got a label THIS run (0 if the class already had
  // everyone numbered and nobody new became active since).
  processedClasses: Array<{ classId: string; name: string; studentsNumbered: number; newlyNumbered: number }>
  skippedClasses: Array<{ classId: string; name: string; reason: string }>
  logId?: string | null
}

export type RollNumberRow = {
  _id: string
  rollNumber: string
  strategy: "section-wise" | "whole-class"
  class: { _id: string; name: string }
  section: { _id: string; name: string }
  grNumber?: string
  studentName: string
  fatherName: string | null
  status?: string
  statusLabel?: string
  isActive?: boolean
}

export type RollNumberLogItem = {
  _id: string
  resultBreakdown: string
  class: { _id: string; name: string } | null
  action: "generate" | "regenerate" | "append"
  strategy: "section-wise" | "whole-class"
  studentsCount: number
  totalCount: number
  reason: string
  performedBy: { _id: string; firstName: string; lastName: string; email?: string }
  createdAt: string
}

export type GenerateRollNumbersOptions = {
  force?: boolean
  classId?: string
  reason?: string
}

const ROLL_NUMBER_ENDPOINT = {
  BASE: "/roll-number",
  GENERATE: "/roll-number/generate",
  HISTORY: "/roll-number/history",
}

// Generate, append, or regenerate roll numbers for a breakdown (or specific class)
const generateRollNumbers = async (
  breakdownId: string,
  options?: GenerateRollNumbersOptions | boolean
): Promise<GenerateRollNumbersResult> => {
  const payload: any = { breakdownId }
  if (typeof options === "boolean") {
    payload.force = options
  } else if (options) {
    if (options.force !== undefined) payload.force = options.force
    if (options.classId) payload.classId = options.classId
    if (options.reason) payload.reason = options.reason
  }

  const response = await apiClient({
    url: ROLL_NUMBER_ENDPOINT.GENERATE,
    method: "POST",
    data: payload,
  })
  return response.data.data
}

const getRollNumbers = async ({
  breakdownId,
  classId,
  sectionId,
}: {
  breakdownId: string
  classId?: string
  sectionId?: string
}): Promise<RollNumberRow[]> => {
  const response = await apiClient({
    url: appendQueryParams(ROLL_NUMBER_ENDPOINT.BASE, {
      breakdownId,
      classId,
      sectionId,
    }),
    method: "GET",
  })
  return response.data.data
}

const getRollNumberLogs = async ({
  breakdownId,
  classId,
}: {
  breakdownId: string
  classId?: string
}): Promise<RollNumberLogItem[]> => {
  const response = await apiClient({
    url: appendQueryParams(ROLL_NUMBER_ENDPOINT.HISTORY, {
      breakdownId,
      classId,
    }),
    method: "GET",
  })
  return response.data.data
}

export { generateRollNumbers, getRollNumbers, getRollNumberLogs }
